import { Assert } from "../../Assert";
import { DataSource } from "../../Data/DataSource";
import { PartialNullable } from "../../Extensions";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { IJNativeEvent } from "../../JNative";
import { Property } from "../../RadObject";
import { AjaxError } from "../../Server/AjaxError";
import { IAjaxRequest } from "../../Server/IAjaxRequest";
import { WebApiClientService } from "../../Server/WebApiClientService";
import { ServiceManager } from "../../ServiceManager";
import { Utility } from "../../Utility";
import { DialogResultEnum } from "../Dialogs/DialogResultEnum";
import { Accordion } from "./Accordion";
import { AccordionPanel } from "./AccordionPanel";
import { Button } from "./Button";
import { ButtonStyleEnum } from "./ButtonStyleEnum";
import { ControlRef } from "./ControlRef";
import { Dialog, DialogPropertyValues, IDialog } from "./Dialog";
import { Form } from "./Form";
import { MessageDialog } from "./MessageDialog";
import { PropertyEditor } from "./PropertyEditor";
import { PropertyEditorDateTime } from "./PropertyEditorDateTime";
import { PropertyEditorNumericUpDown } from "./PropertyEditorNumericUpDown";
import { PropertyEditorSwitch } from "./PropertyEditorSwitch";
import { PropertyEditorTextBox } from "./PropertyEditorTextBox";
import { PropertyEditorTextBoxPassword } from "./PropertyEditorTextBoxPassword";
import { ValidationPanel } from "./ValidationPanel";
import { NAMESPACE } from "./_Namespace";

interface UserSetting
{
    id?: string,
    userSettingTypeId: string,
    value: any
}

interface NewUserSetting
{
    userSetting: UserSetting,
    propertyEditor: PropertyEditor
}

export type UserAccountDialogPropertyValues =
    DialogPropertyValues
    & {
    };

export type UserAccountDialogProperties = {
    [name in keyof UserAccountDialogPropertyValues]: Property<UserAccountDialogPropertyValues[name]>
};

export interface IUserAccountDialog extends IDialog, UserAccountDialogProperties
{
}

export class UserAccountDialog extends Dialog implements IUserAccountDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "UserAccountDialog";
    public static override className = "UserAccountDialog";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<UserAccountDialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                buttonStyle: ButtonStyleEnum.okCancel,
                cancelButtonText: "Close",
                okButtonText: "Save",
                title: "My Account",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _accordionControlRef!: ControlRef<Accordion>;
    protected _accordionPanelChangePasswordControlRef!: ControlRef<AccordionPanel>;
    protected _accordionPanelSettingsControlRef!: ControlRef<AccordionPanel>;
    protected _buttonChangePasswordControlRef!: ControlRef<Button>;
    protected _formChangePasswordControlRef!: ControlRef<Form>;
    protected _loadedUserSettingTypes: boolean;
    protected _messageDialog?: MessageDialog;
    protected _textBoxNewPasswordControlRef!: ControlRef<PropertyEditorTextBoxPassword>;
    protected _textBoxConfirmNewPasswordControlRef!: ControlRef<PropertyEditorTextBoxPassword>;
    protected _textBoxCurrentPasswordControlRef!: ControlRef<PropertyEditorTextBoxPassword>;
    protected _userSettings: any;
    protected _validationPanelChangePasswordControlRef!: ControlRef<ValidationPanel>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<UserAccountDialogPropertyValues>)
    {
        super(propertyValues);

        this._loadedUserSettingTypes = false;
        this._userSettings = null;
    }

    // #endregion

    // #region Public Methods

    public async saveAsync()
    {
        const
            dataSource = this.dataSource.get(),
            data = dataSource?.data.get(),
            dataOriginal = dataSource?.dataOriginal.get(),
            requests: IAjaxRequest[] = [];

        if (!data || !data.length)
            return Promise.resolve();

        data.forEach((item: any) =>
        {
            const
                itemOriginal = dataOriginal.singleOrDefault((itemInner: any) => itemInner.id === item.id);

            if (itemOriginal?.value === item.value)
                return;

            requests.push(
                ServiceManager.getRequired(WebApiClientService)
                    .createEntitySaveRequest("UserSettings", item, itemOriginal)
            );
        });

        const
            response =
                await
                    ServiceManager.getRequired(WebApiClientService)
                        .createBatchRequest(requests)
                        .executeAsync();

        dataSource?.data.set(response.select((item: any) => item.response));
    }

    // #endregion

    // #region Protected Methods

    protected _createNewUserSettingAsync(userSettingTypeId: string, userSettingsArray: any[], propertyEditor: PropertyEditor)
    {
        return new Promise<NewUserSetting>(resolve =>
        {
            const
                userSetting: UserSetting = { userSettingTypeId: userSettingTypeId, value: null };

            Utility.instance
                .newGuidAsync()
                .then(guid =>
                {
                    userSetting.id = guid;
                    userSettingsArray.push(userSetting);
                    resolve({ userSetting: userSetting, propertyEditor: propertyEditor });
                });
        });
    }
    protected _loadUserSettingsAsync()
    {
        return (
            ServiceManager.getRequired(WebApiClientService)
                .createEntitySearchRequest(
                    "UserSettings",
                    {
                        columns: ["id", "rowVersion", "userSettingTypeId", "value"],
                    }
                )
                .executeAsync()
                .then(data =>
                {
                    this.dataSource.set(new DataSource({ data }));
                })
        );
    }
    protected _loadUserSettingTypesAsync()
    {
        return (
            ServiceManager.getRequired(WebApiClientService)
                .createEntitySearchRequest(
                    "UserSettingTypes",
                    {
                        columns: ["id", "name", "dataTypeLookupItem.description"],
                    }
                )
                .executeAsync()
                .then(data =>
                {
                    data.forEach((item: any) =>
                    {
                        let
                            propertyEditorConstructor;

                        switch (item.dataTypeLookupItem.description.toLowerCase())
                        {
                            case "boolean":
                                propertyEditorConstructor = PropertyEditorSwitch;
                                break;
                            case "date":
                                propertyEditorConstructor = PropertyEditorDateTime;
                                break;
                            case "float":
                            case "int":
                                propertyEditorConstructor = PropertyEditorNumericUpDown;
                                break;
                            case "string":
                                propertyEditorConstructor = PropertyEditorTextBox;
                                break;
                        }

                        if (propertyEditorConstructor)
                            new propertyEditorConstructor({ propertyDisplayName: item.name, editMode: true, dataSourceProperty: "value", dataSourcePropertyType: "System.String, System.Private.CoreLib", tag: item }).appendTo(this._accordionPanelSettingsControlRef.current);
                    });

                    if (data?.length)
                    {
                        this._accordionPanelSettingsControlRef.current?.show();
                        this._accordionPanelSettingsControlRef.current?.open();
                        this.buttonStyle.set(ButtonStyleEnum.okCancel);
                    }
                    else
                    {
                        this._accordionPanelSettingsControlRef.current?.hide();
                        this._accordionPanelChangePasswordControlRef.current?.open();
                        this.buttonStyle.set(ButtonStyleEnum.cancelOnly);
                    }

                    this._loadedUserSettingTypes = true;
                })
        );
    }
    protected _onPasswordTextBoxKeyUp(evt: IJNativeEvent<KeyboardEvent>)
    {
        switch (evt.originalEvent.key)
        {
            case "Enter":
                {
                    evt.stopPropagation();
                    this._buttonChangePasswordControlRef.current?.click();
                    break;
                }
        }
    }
    protected _updatePropertyEditorDataSourcesAsync()
    {
        const
            promises: Promise<any>[] = [],
            dataSource = this.dataSource.get();

        if (!dataSource)
            return;

        const
            data = dataSource.data.get();

        if (!data)
            return;

        this._accordionPanelSettingsControlRef.current
            ?.controlsGet(true)
            .filter((item): item is PropertyEditor => item instanceof PropertyEditor)
            .forEach(item =>
            {
                const
                    tag = item.tag.get(),
                    userSetting = data.singleOrDefault((userSetting: any) => userSetting.userSettingTypeId === tag.id);

                if (!userSetting)
                {
                    promises.push(
                        this._createNewUserSettingAsync(tag.id, data, item)
                            .then(result =>
                            {
                                const
                                    userSetting = result.userSetting,
                                    propertyEditor = result.propertyEditor;

                                propertyEditor.dataSource.set(new DataSource({ data: userSetting }));
                            })
                    );
                }
                else
                    item.dataSource.set(userSetting);
            });

        return Promise.all(promises);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: UserAccountDialogPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._accordionControlRef = new ControlRef();
        this._accordionPanelChangePasswordControlRef = new ControlRef();
        this._accordionPanelSettingsControlRef = new ControlRef();
        this._buttonChangePasswordControlRef = new ControlRef();
        this._formChangePasswordControlRef = new ControlRef();
        this._textBoxNewPasswordControlRef = new ControlRef();
        this._textBoxConfirmNewPasswordControlRef = new ControlRef();
        this._textBoxCurrentPasswordControlRef = new ControlRef();
        this._validationPanelChangePasswordControlRef = new ControlRef();
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this._textBoxNewPasswordControlRef.current?.on("keyup", { self: this }, this.onTextBoxNewPasswordKeyUp);
        this._textBoxConfirmNewPasswordControlRef.current?.on("keyup", { self: this }, this.onTextBoxConfirmNewPasswordKeyUp);
        this._textBoxCurrentPasswordControlRef.current?.on("keyup", { self: this }, this.onTextBoxCurrentPasswordKeyUp);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this._buttonChangePasswordControlRef.current?.on("click", { self: this }, this.onChangePasswordClicked);

        return $$element;
    }
    protected override _createChildrenHtml()
    {
        return (
            <Accordion ref={this._accordionControlRef}>
                <AccordionPanel ref={this._accordionPanelSettingsControlRef} text="Settings">
                </AccordionPanel>
                <AccordionPanel ref={this._accordionPanelChangePasswordControlRef} text="Change Password">
                    <Form ref={this._formChangePasswordControlRef}>
                        <ValidationPanel ref={this._validationPanelChangePasswordControlRef}>
                        </ValidationPanel>
                        <PropertyEditorTextBoxPassword ref={this._textBoxNewPasswordControlRef} propertyDisplayName="New Password" editMode={true} />
                        <PropertyEditorTextBoxPassword ref={this._textBoxConfirmNewPasswordControlRef} propertyDisplayName="Confirm New Password" editMode={true} />
                        <PropertyEditorTextBoxPassword ref={this._textBoxCurrentPasswordControlRef} propertyDisplayName="Current Password" editMode={true} />
                        <Button ref={this._buttonChangePasswordControlRef} cssClass="primary" text="Change Password" />
                    </Form>
                </AccordionPanel>
            </Accordion>
        );
    }
    protected override _loadAsync()
    {
        const
            promises = [];

        if (!this._loadedUserSettingTypes)
            promises.push(this._loadUserSettingTypesAsync());

        promises.push(this._loadUserSettingsAsync());

        return (
            Promise.all(promises)
                .then(() =>
                {
                    return this._updatePropertyEditorDataSourcesAsync();
                })
                .then(() =>
                {
                    return super._loadAsync();
                })
        );
    }

    public override async closeAsync()
    {
        if (this.dialogResult === DialogResultEnum.ok)
            await this.saveAsync();

        await super.closeAsync();
    }

    // #endregion

    // #region Event Handlers

    protected onChangePasswordClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        Assert.isNotNullOrUndefined(self._validationPanelChangePasswordControlRef.current, "self._validationPanelChangePasswordControlRef.current");
        Assert.isNotNullOrUndefined(self._textBoxNewPasswordControlRef.current, "self._textBoxNewPasswordControlRef.current");
        Assert.isNotNullOrUndefined(self._textBoxConfirmNewPasswordControlRef.current, "self._textBoxConfirmNewPasswordControlRef.current");
        Assert.isNotNullOrUndefined(self._textBoxCurrentPasswordControlRef.current, "self._textBoxCurrentPasswordControlRef.current");

        self._validationPanelChangePasswordControlRef.current.setErrorMessages(null);
        self._textBoxNewPasswordControlRef.current.errorState.set(false);
        self._textBoxConfirmNewPasswordControlRef.current.errorState.set(false);
        self._textBoxCurrentPasswordControlRef.current.errorState.set(false);

        const
            newPassword = self._textBoxNewPasswordControlRef.current.value.get(),
            confirmPassword = self._textBoxConfirmNewPasswordControlRef.current.value.get(),
            currentPassword = self._textBoxCurrentPasswordControlRef.current.value.get();

        let
            valid = true;

        if (!newPassword || newPassword.length === 0 || newPassword !== confirmPassword)
        {
            self._textBoxNewPasswordControlRef.current.errorState.set(true);
            valid = false;
        }

        if (!confirmPassword || confirmPassword.length === 0 || newPassword !== confirmPassword)
        {
            self._textBoxConfirmNewPasswordControlRef.current.errorState.set(true);
            valid = false;
        }

        if (!currentPassword || currentPassword.length === 0)
        {
            self._textBoxCurrentPasswordControlRef.current.errorState.set(true);
            valid = false;
        }

        if (!valid)
            return;

        ServiceManager.getRequired(WebApiClientService)
            .createApiMethodRequest("DataService", "CallDataService", { id: "a8a7e53b-6cf2-4324-86e4-83ec7c41bf01", operation: "ChangePassword", data: ObjectHelper.toJson({ newPassword: newPassword, currentPassword: currentPassword }) })
            .executeAsync()
            .then(data =>
            {
                if (!self._messageDialog)
                    self._messageDialog = new MessageDialog();

                self._messageDialog.title.set("Success");
                self._messageDialog.text.set("Password changed successfully.");

                self._messageDialog
                    .openAsync()
                    .then(() =>
                    {
                        Assert.isNotNullOrUndefined(self._textBoxNewPasswordControlRef.current, "self._textBoxNewPasswordControlRef.current");
                        Assert.isNotNullOrUndefined(self._textBoxConfirmNewPasswordControlRef.current, "self._textBoxConfirmNewPasswordControlRef.current");
                        Assert.isNotNullOrUndefined(self._textBoxCurrentPasswordControlRef.current, "self._textBoxCurrentPasswordControlRef.current");

                        self._textBoxNewPasswordControlRef.current.value.set(null);
                        self._textBoxConfirmNewPasswordControlRef.current.value.set(null);
                        self._textBoxCurrentPasswordControlRef.current.value.set(null);
                    });
            })
            .catch(error =>
            {
                if (error instanceof AjaxError && error.type.get() === "ValidationApiException")
                {
                    Assert.isNotNullOrUndefined(self._validationPanelChangePasswordControlRef.current, "self._validationPanelChangePasswordControlRef.current");
                    self._validationPanelChangePasswordControlRef.current.setErrorMessages(error.validationErrors.get()?.map(item => item.errorMessage) ?? []);
                }
            });
    }
    protected onTextBoxConfirmNewPasswordKeyUp(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this;

        self._onPasswordTextBoxKeyUp(evt);
    }
    protected onTextBoxCurrentPasswordKeyUp(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this;

        self._onPasswordTextBoxKeyUp(evt);
    }
    protected onTextBoxNewPasswordKeyUp(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this;

        self._onPasswordTextBoxKeyUp(evt);
    }

    // #endregion

    // #endregion
}
